import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  FlaskConical,
  Clock,
  Download,
  CheckCircle,
  XCircle,
} from "lucide-react";
import api from "../lib/api";
import { useNotifications } from "../contexts/NotificationContext";
import FeatureHeader from "../components/FeatureHeader";
import Loader from "../components/loader";

export default function BookingDetails() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { addNotification } = useNotifications();
  const notifiedRef = useRef(false);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const res = await api.get(`/api/lab-bookings/${bookingId}`);
        setBooking(res.data.booking);
      } catch (err) {
        setError("Failed to load booking details.");
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId]);

  // Notify once when a completed result is detected for this booking
  useEffect(() => {
    if (!booking || notifiedRef.current) return;
    const result = booking.tests[0]?.result;
    const testName = booking.tests[0]?.testId?.name || "your lab test";
    if (result?.status === "completed" || result?.resultFile) {
      const storageKey = `qureo.notifiedResult_${bookingId}`;
      if (!localStorage.getItem(storageKey)) {
        addNotification({
          type: "lab_result",
          title: "Lab Result Available",
          body: `Results for ${testName} are ready. Tap to download.`,
          icon: "📋",
        });
        localStorage.setItem(storageKey, "1");
      }
      notifiedRef.current = true;
    }
  }, [booking, bookingId, addNotification]);

  if (loading)
    return (
     <Loader />
    );

  if (error || !booking)
    return <div className="text-center py-5 text-danger">{error}</div>;

  const test = booking.tests[0]?.testId;
  const result = booking.tests[0]?.result;

  const handleDownload = () => {
    if (!result?.resultFile) return;
    const link = document.createElement("a");
    link.href = result.resultFile;
    link.download = `${test?.name || "LabResult"}.png`;
    link.click();
  };

  return (
    <div
      style={{
        background: "#f7f8fa",
        minHeight: "100vh",
        paddingBottom: 40,
      }}
    >
      {/* Header */}
        <FeatureHeader title = "Booking Details" subtitle = "See Lab test details" onBack={() => navigate(-1)} />

      <div className="px-3 mt-3" style={{ maxWidth: 720, margin: "0 auto" }}>
        {/* Summary */}
        <div className="bg-white p-3 mb-3 rounded-4 shadow-sm">
          <h6 className="fw-semibold mb-2" style = {{color : "var(--primary)"}}>Booking Summary</h6>

          <div className="d-flex justify-content-between mb-1">
            <span>Status</span>
            <span className="fw-semibold">
              {result?.status === "completed" ? (
                <span className="text-success d-flex gap-1 align-items-center">
                  <CheckCircle size={14} /> Completed
                </span>
              ) : (
                <span className="text-warning">Pending</span>
              )}
            </span>
          </div>

          <div className="d-flex justify-content-between mb-1">
            <span>Collection</span>
            <span>{booking.collectionMethod}</span>
          </div>

          <div className="d-flex justify-content-between mb-1">
            <span>Payment</span>
            <span
              className={
                booking.paymentStatus === "paid"
                  ? "text-success"
                  : "text-danger"
              }
            >
              {booking.paymentStatus}
            </span>
          </div>

          <div className="d-flex justify-content-between">
            <span>Total</span>
            <strong>${booking.totalAmount.toFixed(2)}</strong>
          </div>
        </div>

        {/* Test Info */}
        {test && (
          <div className="bg-white p-3 mb-3 rounded-4 shadow-sm">
            <h6 style = {{color : "var(--primary)"}} className="fw-semibold mb-3 d-flex gap-2 align-items-center">
              <FlaskConical size={18} color = "var(--primary)" /> Test Information
            </h6>

            {test.image && (
              <img
                src={test.image}
                alt={test.name}
                className="img-fluid rounded-3 mb-3"
                style={{ height: 280, objectFit: "cover", width: "100%" }}
              />
            )}

            <p className="mb-1">
              <strong>Name:</strong> {test.name}
            </p>
            <p className="mb-1">
              <strong>Category:</strong> {test.category}
            </p>
            <p className="mb-1">
              <strong>Laboratory:</strong> {test.laboratory}
            </p>
            <p className="mb-1">
              <strong>Preparation:</strong> {test.preparation}
            </p>
            <p className="mb-0">
              <strong>Estimated Time:</strong> {test.estimatedTime}
            </p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-white p-3 mb-4 rounded-4 shadow-sm">
            <h6 style = {{color : "var(--primary)"}} className="fw-semibold mb-3 d-flex gap-2 align-items-center">
              <Clock size={18} /> Test Result
            </h6>

            <p className="mb-2">
              <strong>Status:</strong> {result.status}
            </p>

            {result.resultFile && (
              <>
                <img
                  src={result.resultFile}
                  alt="Result"
                  className="w-100 rounded-3 mb-3"
                  style={{ maxHeight: 400, objectFit: "contain" }}
                />

                <button
                  onClick={handleDownload}
                  className="btn w-100 d-flex justify-content-center align-items-center gap-2"
                  style={{
                    background: "#704af6",
                    color: "#fff",
                    borderRadius: 12,
                    padding: "12px 0",
                  }}
                >
                  <Download size={18} /> Download Result
                </button>
              </>
            )}

            {result.remarks && (
              <p className="mt-3 mb-0 text-muted">
                <strong>Remarks:</strong> {result.remarks}
              </p>
            )}
          </div>
        )}

        {/* CTA */}
        <button
          className="btn w-100"
          style={{
            background: "#ece9ff",
            color: "#704af6",
            borderRadius: 12,
            padding: "12px 0",
            fontWeight: 600,
          }}
          onClick={() => navigate("/lab-tests")}
        >
          Back to Tests
        </button>
      </div>
    </div>
  );
}
